import { useMemo, useState } from "react";
import { TrendingUp, AlertTriangle, Plus } from "lucide-react";
import { LEVELS, tileCapFor, levelMeta } from "@/lib/boardLevels";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * Board Growth Level picker — lives inside Manage Board → Create a Button.
 *
 * Behavior:
 *   - Reads `profile.board_level`. If unset/grandfathered, shows a friendly
 *     non-disruptive banner ("This board can now be organized into growth
 *     levels…") and the picker — but no auto-reset.
 *   - Switching levels never deletes tiles. If the new cap is smaller than
 *     the current tile count, we warn the parent that extras will be HIDDEN
 *     (not removed) and they can raise the level again later to bring them
 *     back.
 *   - "Add recommended tiles for this level" hits the additive backend
 *     endpoint that inserts any missing template tiles. Safe to call
 *     repeatedly — already-present labels are skipped.
 */
export default function BoardLevelCard({ profile, tileCount, onChanged }) {
  const current = profile.board_level || null;
  const grandfathered = !current;
  const [selected, setSelected] = useState(current || "starter6");
  const [sbEnabled, setSbEnabled] = useState(profile.sentence_builder_enabled === true);
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [error, setError] = useState("");
  const [addCount, setAddCount] = useState(null);

  const selectedMeta = useMemo(() => levelMeta(selected), [selected]);
  const sbAvailable = selectedMeta?.sentenceBuilder === true;

  // Warn when the new cap would hide currently-visible tiles. We can't
  // know which tiles get hidden (the cap applies after filtering on the
  // Board), so we surface a soft "extras will be hidden" copy instead of
  // a specific count.
  const newCap = tileCapFor(selected);
  const willHide = newCap != null && tileCount > newCap;

  const save = async () => {
    setBusy(true);
    setError("");
    setSavedMsg("");
    try {
      await api.put(`/profiles/${profile.id}`, {
        ...profile,
        board_level: selected,
        sentence_builder_enabled: sbAvailable ? sbEnabled : false,
      });
      setSavedMsg(`Saved — board is now ${selectedMeta?.label}.`);
      onChanged?.();
      setTimeout(() => setSavedMsg(""), 2400);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not save level.");
    } finally {
      setBusy(false);
    }
  };

  const addRecommended = async () => {
    setBusy(true);
    setError("");
    setAddCount(null);
    try {
      const r = await api.post(`/profiles/${profile.id}/board-level/add-recommended`);
      setAddCount(r.data?.tiles_added ?? 0);
      onChanged?.();
    } catch (e) {
      setError(
        formatApiErrorDetail(e.response?.data?.detail) ||
          "Could not add the recommended tiles."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="brutal-card bg-[color:var(--gold)]/30 p-4 sm:p-5 mb-5"
      data-testid="manage-board-level-card"
    >
      <header className="flex items-start gap-2 mb-2">
        <TrendingUp size={20} className="shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-extrabold text-base">Board Growth Level</h3>
          <p className="text-xs text-[color:var(--ink)]/70">
            Start small. Grow the board as your child is ready.
          </p>
        </div>
      </header>

      {grandfathered && (
        <div
          className="brutal-card bg-white p-3 text-sm mb-3"
          data-testid="manage-board-level-grandfathered-banner"
        >
          This board can now be organized into growth levels: 6, 12, 18, 24,
          or 30 buttons — or <strong>Show all</strong> for no cap. Choose a
          level when you're ready — your current tiles stay exactly where
          they are until you pick.
        </div>
      )}

      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3"
        role="radiogroup"
        aria-label="Board growth level"
      >
        {LEVELS.map((l) => {
          const active = selected === l.id;
          return (
            <button
              key={l.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setSelected(l.id)}
              className={`brutal-card text-left p-3 ${active ? "bg-[color:var(--gold)]" : "bg-white"}`}
              data-testid={`manage-board-level-option-${l.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="font-extrabold text-sm">{l.label}</span>
                {l.sentenceBuilder && (
                  <span
                    className="text-[10px] uppercase tracking-[0.16em] bg-[color:var(--ink)] text-white px-1.5 py-0.5 rounded font-bold"
                    title="Sentence Builder is available at this level"
                  >
                    + sentence builder
                  </span>
                )}
              </div>
              <div className="text-xs text-[color:var(--ink)]/70 leading-snug">
                {l.tiles == null ? "No cap" : `${l.tiles} buttons`} · {l.blurb}
              </div>
            </button>
          );
        })}
      </div>

      {willHide && (
        <div
          className="brutal-card bg-amber-50 p-3 text-sm flex items-start gap-2 mb-3 text-amber-900"
          data-testid="manage-board-level-hide-warning"
        >
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            This board currently has <strong>{tileCount} tiles</strong> but
            <strong> {selectedMeta?.label}</strong> shows only{" "}
            <strong>{newCap}</strong>. Your extra tiles will be{" "}
            <strong>hidden, not deleted</strong> — raise the level again any
            time to bring them back.
          </div>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 mb-2" data-testid="manage-board-level-error">
          {error}
        </div>
      )}
      {savedMsg && (
        <div
          className="text-sm text-emerald-700 font-bold mb-2"
          data-testid="manage-board-level-saved"
        >
          {savedMsg}
        </div>
      )}
      {addCount !== null && (
        <div
          className="text-sm text-emerald-700 font-bold mb-2"
          data-testid="manage-board-level-added"
        >
          {addCount > 0
            ? `Added ${addCount} recommended tile${addCount === 1 ? "" : "s"}.`
            : "All recommended tiles are already on this board."}
        </div>
      )}

      {sbAvailable && (
        <label
          className="brutal-card bg-white p-3 flex items-start gap-2 mb-3 cursor-pointer"
          data-testid="manage-board-level-sentence-builder"
        >
          <input
            type="checkbox"
            checked={sbEnabled}
            onChange={(e) => setSbEnabled(e.target.checked)}
            className="w-5 h-5 accent-[color:var(--coral)] mt-0.5"
            data-testid="manage-board-level-sentence-builder-checkbox"
          />
          <div className="text-sm">
            <div className="font-bold">Turn on Sentence Builder</div>
            <div className="text-xs text-[color:var(--ink)]/70">
              Sentence frames ("I want ___", "I feel ___") appear on the
              child's board so they can combine tiles into phrases. Leave OFF
              until your child is ready — you can flip this on any time.
            </div>
          </div>
        </label>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-end">
        {/* "Add recommended tiles" hidden per founder spec (2026-05-24) —
            example tiles are no longer auto-seeded. Parents build the
            board themselves with the Create-new wizard. */}
        <button
          type="button"
          onClick={save}
          disabled={busy || (selected === current && sbEnabled === (profile.sentence_builder_enabled === true))}
          className="pill-btn primary"
          data-testid="manage-board-level-save"
        >
          {busy ? "Saving…" : grandfathered ? "Set level" : "Update level"}
        </button>
      </div>
    </section>
  );
}
